import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Heart, Users, AlertTriangle, Flag, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const CommunityGuidelines = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-rose-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Community Guidelines</h1>
          <p className="text-gray-600 mt-2">Building a supportive sisterhood together</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Card className="p-8 mb-6">
          <div className="prose prose-rose max-w-none">
            <div className="flex items-start space-x-4 mb-6 bg-rose-50 p-6 rounded-lg border-2 border-rose-200">
              <Heart className="w-8 h-8 text-rose-500 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mt-0">Welcome to Our Sacred Space 💕</h2>
                <p className="text-gray-700 mb-0">
                Our community is a safe space where Constant Love users support each other on their self-love journeys. 
                  These guidelines help us maintain a nurturing environment for everyone.
                </p>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="flex items-center text-2xl font-bold text-gray-900 mb-4">
                <Star className="w-6 h-6 mr-2 text-yellow-500" />
                Core Values
              </h2>
              <ul className="space-y-3">
                <li><strong>Compassion First:</strong> Approach every interaction with empathy and understanding</li>
                <li><strong>Authenticity:</strong> Be your genuine self while respecting others' journeys</li>
                <li><strong>Growth Mindset:</strong> We're all works in progress learning together</li>
                <li><strong>Mutual Support:</strong> Lift each other up, celebrate wins, offer comfort in struggles</li>
                <li><strong>Privacy & Safety:</strong> What's shared here stays here</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center text-2xl font-bold text-gray-900 mb-4">
                <Heart className="w-6 h-6 mr-2 text-rose-500" />
                Do's - Ways to Show Up 💫
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>✅ Share your personal experiences and insights from your self-love journey</li>
                <li>✅ Offer encouragement and celebrate others' wins, big and small</li>
                <li>✅ Ask for support when you need it - vulnerability is strength</li>
                <li>✅ Respect different perspectives, backgrounds, and healing timelines</li>
                <li>✅ Use content warnings when discussing sensitive topics</li>
                <li>✅ Sharing insights from your Conchi conversations (if comfortable)</li>
                <li>✅ Recommend resources that helped you (books, podcasts, practices)</li>
                <li>✅ Report content that violates guidelines to keep our space safe</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center text-2xl font-bold text-gray-900 mb-4">
                <AlertTriangle className="w-6 h-6 mr-2 text-red-500" />
                Don'ts - Behaviors We Don't Allow 🚫
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>❌ Harassment, bullying, or attacking other members</li>
                <li>❌ Discrimination based on race, gender, sexuality, religion, or any identity</li>
                <li>❌ Unsolicited advice or "fixing" others' problems</li>
                <li>❌ Sharing others' private conversations with Conchi</li>
                <li>❌ Promoting products, services, or MLM schemes</li>
                <li>❌ Spam, excessive self-promotion, or off-topic content</li>
                <li>❌ Graphic content, triggering imagery without warnings</li>
                <li>❌ Medical, legal, or financial advice (share experiences, not prescriptions)</li>
                <li>❌ Trolling, gaslighting, or toxic positivity</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center text-2xl font-bold text-gray-900 mb-4">
                <Users className="w-6 h-6 mr-2 text-blue-500" />
                Posting Guidelines
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Choose the Right Channel</h3>
              <ul className="space-y-2 text-gray-700">
                <li>📢 <strong>Announcements:</strong> Official updates from the team (read-only)</li>
                <li>💕 <strong>Daily Check-ins & Wins:</strong> Share daily victories and check-ins</li>
                <li>🌱 <strong>Growth Stories:</strong> Transformation moments and breakthroughs</li>
                <li>💪 <strong>Accountability Partners:</strong> Find your support buddy</li>
                <li>❓ <strong>Ask the Community:</strong> Get advice and support</li>
                <li>💭 <strong>Reflections:</strong> Share insights from your practice</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Create Quality Posts</h3>
              <ul className="space-y-2 text-gray-700">
                <li>Be specific and clear in your posts</li>
                <li>Use descriptive titles that help others find relevant content</li>
                <li>Add context to help others understand your situation</li>
                <li>Share tips for getting the most out of Constant Love</li>
                <li>Engage with replies and thank those who support you</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Feedback & Suggestions</h3>
              <ul className="space-y-2 text-gray-700">
                <li>Share constructive feedback about app features</li>
                <li>Propose new features for Constant Love</li>
                <li>Report bugs or technical issues</li>
                <li>Always frame feedback positively and constructively</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center text-2xl font-bold text-gray-900 mb-4">
                <Shield className="w-6 h-6 mr-2 text-green-500" />
                Safety & Privacy
              </h2>
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <ul className="space-y-3 text-gray-700">
                  <li><strong>Your Privacy Matters:</strong> Never share personal identifying information (phone, address, last name)</li>
                  <li><strong>Respect Confidentiality:</strong> Don't screenshot or share posts outside the community</li>
                  <li><strong>Crisis Support:</strong> If you're in crisis, please contact professional help (988 Suicide & Crisis Lifeline)</li>
                  <li><strong>Report Concerns:</strong> Use the report feature for any guideline violations</li>
                  <li><strong>Block & Mute:</strong> Use these tools to curate your experience</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center text-2xl font-bold text-gray-900 mb-4">
                <Flag className="w-6 h-6 mr-2 text-orange-500" />
                Reporting & Moderation
              </h2>
              <p className="text-gray-700 mb-4">
                We review all reports within 24-48 hours. Violations may result in:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>⚠️ <strong>First Offense:</strong> Warning and education about guidelines</li>
                <li>⏸️ <strong>Repeated Violations:</strong> Temporary suspension from community</li>
                <li>🚫 <strong>Severe Violations:</strong> Permanent ban from community (harassment, hate speech, spam)</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Appeals can be sent to our support team within 7 days.
              </p>
            </section>

            <div className="bg-rose-50 p-6 rounded-lg border-2 border-rose-200 mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Remember 💖</h3>
              <p className="text-gray-700 mb-2">
                This community exists to support your self-love journey. Every member deserves to feel safe, 
                seen, and supported. By following these guidelines, you help create the nurturing space we all need.
              </p>
              <p className="text-gray-700 font-semibold">
                Let's build each other up, one conversation at a time. 🌸
              </p>
            </div>

            <div className="text-center mt-8 text-sm text-gray-500">
              <p>Last updated: January 2025</p>
              <p>Questions? Contact us at support@constantloveco.com</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CommunityGuidelines;